import React, { useState } from 'react';
import { useAuth } from './common/Auth';
import { useSystems, useGuestSystems, useCurrentSystem } from './context/SystemContext';
import SystemCard from './SystemCard';
import SystemEditor from './SystemEditor';
import ConfirmDialog from './ConfirmDialog';
import { DEFAULT_TIMEZONE } from './systemSettingsUtils';
import { Plus } from 'lucide-react';

const SystemList = ({ deleteSystem, forgetSystem, onSystemSelected }) => {
	const currentUser = useAuth();
	const { systems = [] } = useSystems(currentUser?.uid, 50);
	const { guestSystems = [] } = useGuestSystems(currentUser?.uid, 50);
	const { setCurrentSystemUID } = useCurrentSystem();
	const [creating, setCreating] = useState(false);
	const [editing, setEditing] = useState(null);
	const [deleteConfirm, setDeleteConfirm] = useState(null);

	const handleNew = () => setCreating(true);

	const openEdit = (sys) => setEditing(sys);

	const handleDelete = (sys) => setDeleteConfirm(sys);

	const handleSelectSystem = (uid) => {
		if (onSystemSelected) {
			onSystemSelected(uid);
		} else {
			setCurrentSystemUID(uid);
		}
	};

	return (
		<div className="p-4 sm:p-6 max-w-6xl mx-auto">
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
				<div>
					<h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Systems</h1>
					<p className="text-sm text-gray-500 mt-0.5">Manage your lighting control systems</p>
				</div>
				<button
					onClick={handleNew}
					className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#880e4f] hover:bg-[#6b0b3f] text-white rounded-xl text-sm font-medium shadow-sm transition-colors w-full sm:w-auto"
				>
					<Plus size={16} /> New System
				</button>
			</div>

			<div className="mb-8">
				<div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-500">
					<div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" /> Owned Systems <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
				</div>
				<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
					{systems.length > 0 ? systems.map((sys) => (
						<SystemCard
							key={sys.uid}
							system={sys}
							onClick={() => handleSelectSystem(sys.uid)}
							onEdit={() => openEdit(sys)}
							onDelete={() => handleDelete(sys)}
						/>
					)) : (
						<div className="col-span-full p-8 border border-dashed rounded-2xl text-center text-gray-500 bg-white dark:bg-gray-900">No systems yet. Create your first one to get started.</div>
					)}
				</div>
			</div>

			{guestSystems.length > 0 && (
				<div>
					<div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-500">
						<div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" /> Guest Access <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
					</div>
					<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
						{guestSystems.map((sys) => (
							<SystemCard
								key={sys.uid}
								system={sys}
								onClick={() => handleSelectSystem(sys.uid)}
							/>
						))}
					</div>
				</div>
			)}

			{(creating || editing) && (
				<SystemEditor
					open={true}
					system={editing || { name: 'New System', location: '', timezone: DEFAULT_TIMEZONE }}
					newsystem={creating && !editing}
					onClose={() => { setCreating(false); setEditing(null); }}
				/>
			)}

			{deleteConfirm && (
				<ConfirmDialog
					open={true}
					title="Delete System?"
					message={`Permanently delete "${deleteConfirm.name || deleteConfirm.uid}"?`}
					yes_text="Delete"
					no_text="Cancel"
					on_yes={() => {
						if (deleteSystem) deleteSystem(deleteConfirm);
						setDeleteConfirm(null);
					}}
					on_no={() => setDeleteConfirm(null)}
				/>
			)}
		</div>
	);
};

export default SystemList;
